
// ===========================================================================================================
// table.js — X-Arcade Token Storm playfield definition
//
// Builds the physics World for the table: outline/inner walls (WALL_POLYLINES), every device listed in
// LAYOUT (flippers, slingshots, pop bumpers, lanes, kickbacks, spinner, standups, drop targets, scoop, plunger
// lane), the ramps (RAMP_DEFS) and the lamp/label lists the renderer draws. Coordinates are playfield meters:
// x runs left -> right (0..TABLE.W), y runs from the flipper end (0) up to the back of the table (TABLE.L).
//
// Three traced wall sections that formed ball-trapping pockets are dropped and replaced by single smooth guide
// faces (retirePocketWalls), and a handful of short chamfer/fillet rails open the remaining rest pockets the
// sweep in tests/table-pockets.mjs finds (see POCKET_FIXES). Surfaces come from the MATERIALS table below.
//
// There is one ramp: the left "LOOP" wireform. The right ramp is gone for good - no data, no walls, no
// sensors, no lamp or label.
//
// Four pieces of the traced layout are deliberately re-tuned, each with its own comment: the main flipper bats
// are longer (MAIN_BAT_LEN) so the drain gap is 1.3 balls instead of 1.8; a short guide continues the left
// orbit lane's wall so the loop returns to the left flipper (LEFT_ORBIT_EXIT_GUIDE); the drop bank sits 15 mm
// right of its traced x so the lane up to the spinner and the pop nest is wide enough to shoot; and the third
// left standup (LAYOUT.standL[2]) moved out of that orbit return. Verified by tests/table-shotmap.mjs,
// tests/table-route-trace.mjs, tests/table-outlane.mjs and tests/table-pockets.mjs.
//
// Depends on physics.js: vec, segment, circle, sensorCircle, sensorLine, Flipper, World.
// Public names: WALL_POLYLINES, LAYOUT, RAMP_DEFS, TABLE, DEG, MATERIALS, MAT_RAIL, MAT_RUBBER, buildTable,
//               addRampReturnWalls, computeRampLength, rampTangent, rampProgress.
// Module-local constants (not used outside this file): ROLLING_MU, DAMPING, TOP_LANE_DIVIDERS, MAIN_BAT_LEN,
//               LEFT_ORBIT_EXIT_GUIDE, POCKET_FIXES, slimTopLaneDivider.
// ===========================================================================================================

// table-data.js (generated from the original bundle; keep numbers verbatim)
// Playfield outline/inner wall polylines in playfield meters. Each entry is a polyline of [x, y] points; see
// buildTable() for which are closed loops and which use rubber material.
const WALL_POLYLINES = [
  [
    [0.0007, 0.3244],
    [0.001, 0.3366],
    [0.0033, 0.3486],
    [0.0075, 0.3589],
    [0.0118, 0.3671],
    [0.0169, 0.3743],
    [0.0244, 0.3824],
    [0.0482, 0.3839],
    [0.0493, 0.3855],
    [0.0331, 0.4928],
    [0.0335, 0.4993],
    [0.0333, 0.5059],
    [0.0304, 0.5116],
    [0.0248, 0.5162],
    [0.024, 0.5204],
    [0.024, 0.5254],
    [0.0338, 0.5446],
    [0.0372, 0.5575],
    [0.0375, 0.5685],
    [0.0372, 0.5768],
    [0.0391, 0.5793],
    [0.0427, 0.5812],
    [0.0512, 0.5824],
    [0.0562, 0.5801],
    [0.0574, 0.5774],
    [0.0588, 0.5757],
    [0.0606, 0.5747],
    [0.063, 0.5742],
    [0.0657, 0.5744],
    [0.0709, 0.579],
    [0.0713, 0.5815],
    [0.0712, 0.5846],
    [0.0667, 0.6048],
    [0.0526, 0.6756],
    [0.0402, 0.742],
    [0.0179, 0.8565],
    [0.0112, 0.8967],
    [0.0085, 0.9192],
    [0.0064, 0.9435],
    [0.0055, 0.9668],
    [0.0075, 0.9806],
    [0.0129, 0.9968],
    [0.0217, 1.0145],
    [0.0317, 1.0274],
    [0.0433, 1.0383],
    [0.0605, 1.0494],
    [0.0736, 1.0555],
    [0.1153, 1.0718],
    [0.1419, 1.0807],
    [0.1673, 1.0873],
    [0.1924, 1.0926],
    [0.2193, 1.0974],
    [0.2477, 1.1014],
    [0.2733, 1.1036],
    [0.2945, 1.1044],
    [0.3129, 1.1044],
    [0.3427, 1.1028],
    [0.3607, 1.1004],
    [0.3801, 1.0966],
    [0.3997, 1.0912],
    [0.4184, 1.0845],
    [0.4355, 1.0766],
    [0.4505, 1.0681],
    [0.4624, 1.0593],
    [0.4714, 1.0508],
    [0.4781, 1.043],
    [0.4833, 1.0358],
    [0.4916, 1.0211],
    [0.4966, 1.0092],
    [0.4979, 1.0049],
    [0.4986, 1.0002],
    [0.5, 0.943],
    [0.5004, 0.0668],
  ],
  [
    [0.4122, 0.333],
    [0.4116, 0.2562],
    [0.4091, 0.2425],
    [0.4066, 0.2355],
    [0.4025, 0.2294],
    [0.3984, 0.2256],
    [0.3118, 0.1645],
    [0.3118, 0.1636],
    [0.3179, 0.1558],
    [0.3193, 0.152],
    [0.3219, 0.1405],
    [0.4065, 0.1944],
    [0.4099, 0.2005],
    [0.411, 0.2039],
    [0.4123, 0.2138],
    [0.4138, 0.2471],
    [0.4143, 0.3332],
    [0.414, 0.3343],
    [0.4126, 0.3342],
  ],
  [
    [0.4537, 0.9941],
    [0.4526, 1.0034],
    [0.4498, 1.0116],
    [0.4458, 1.0187],
    [0.441, 1.0251],
    [0.4211, 1.0437],
    [0.4195, 1.0437],
    [0.3948, 1.0361],
    [0.3918, 1.0358],
    [0.3889, 1.034],
    [0.3871, 1.0316],
    [0.3865, 1.029],
    [0.3867, 1.0186],
    [0.3853, 1.0033],
    [0.3823, 0.9923],
    [0.3829, 0.9883],
    [0.3849, 0.9851],
    [0.3887, 0.9832],
    [0.3926, 0.9833],
    [0.4236, 0.9778],
    [0.4351, 0.9489],
    [0.4422, 0.9115],
    [0.4385, 0.8809],
    [0.4395, 0.8796],
    [0.4411, 0.8799],
    [0.4415, 0.8806],
    [0.4534, 0.983],
  ],
  [
    [0.0369, 0.336],
    [0.0354, 0.3327],
    [0.0354, 0.2122],
    [0.0361, 0.2074],
    [0.0381, 0.2024],
    [0.0411, 0.1978],
    [0.0447, 0.1945],
    [0.126, 0.141],
    [0.1296, 0.1521],
    [0.1313, 0.1557],
    [0.1331, 0.1581],
    [0.1386, 0.1635],
    [0.1387, 0.1643],
    [0.0533, 0.2257],
    [0.0476, 0.2308],
    [0.0435, 0.2372],
    [0.0416, 0.2434],
    [0.0403, 0.2544],
    [0.0401, 0.3331],
    [0.0385, 0.3358],
  ],
  [
    [0.2638, 1.068],
    [0.2586, 1.0695],
    [0.1881, 1.053],
    [0.1244, 1.0339],
    [0.077, 1.006],
    [0.0504, 0.9703],
    [0.0442, 0.9435],
    [0.0435, 0.9134],
    [0.049, 0.8761],
    [0.0594, 0.8363],
    [0.1078, 0.6836],
    [0.1186, 0.6396],
    [0.1188, 0.6362],
    [0.12, 0.6329],
    [0.1223, 0.6307],
    [0.125, 0.6298],
    [0.1279, 0.63],
    [0.1309, 0.6311],
    [0.1333, 0.6329],
    [0.1346, 0.6353],
    [0.135, 0.638],
    [0.1348, 0.6408],
    [0.1336, 0.6435],
    [0.1315, 0.6457],
    [0.1048, 0.7035],
  ],
  [
    [0.2366, 1.0143],
    [0.2249, 1.0102],
    [0.2127, 1.0034],
    [0.2014, 0.9924],
    [0.1925, 0.9773],
    [0.1861, 0.9593],
    [0.1813, 0.9388],
    [0.1781, 0.9145],
    [0.1765, 0.8858],
    [0.1757, 0.855],
    [0.1753, 0.8275],
    [0.1763, 0.8034],
    [0.1816, 0.755],
    [0.1815, 0.7482],
    [0.1803, 0.7452],
    [0.1782, 0.7427],
    [0.1737, 0.74],
    [0.1682, 0.7386],
    [0.1653, 0.7393],
    [0.1621, 0.742],
    [0.1602, 0.7468],
    [0.1292, 0.8081],
  ],
  [
    [0.445, 0.3412],
    [0.4464, 0.1163],
    [0.4119, 0.1163],
    [0.4119, 0.1484],
    [0.4112, 0.1508],
    [0.4095, 0.1517],
    [0.4072, 0.151],
    [0.257, 0.0539],
    [0.2546, 0.0516],
    [0.2529, 0.0485],
    [0.2523, 0.0453],
    [0.2523, 0.0051],
    [0.2434, 0.0001],
    [0.2099, 0.0001],
    [0.1935, 0.0095],
    [0.1935, 0.0448],
    [0.1929, 0.0481],
    [0.1912, 0.0513],
    [0.1889, 0.0536],
    [0.0388, 0.1575],
    [0.0365, 0.1583],
    [0.0348, 0.1574],
    [0.0342, 0.1551],
    [0.0342, 0.1167],
    [0.0003, 0.1167],
    [0.0008, 0.3137],
  ],
  [
    [0.2427, 0.7931],
    [0.2476, 0.7604],
    [0.2496, 0.7677],
    [0.2525, 0.7746],
    [0.2563, 0.7812],
    [0.2609, 0.7871],
    [0.2663, 0.7925],
    [0.2723, 0.797],
    [0.2788, 0.8008],
    [0.2858, 0.8037],
    [0.2931, 0.8056],
    [0.3006, 0.8066],
    [0.3082, 0.8066],
    [0.3157, 0.8056],
    [0.323, 0.8036],
    [0.3299, 0.8007],
    [0.3365, 0.7969],
    [0.3424, 0.7923],
    [0.3478, 0.7869],
    [0.3523, 0.7809],
    [0.3561, 0.7744],
    [0.359, 0.7674],
    [0.3609, 0.7601],
    [0.3619, 0.7526],
    [0.3618, 0.7479],
    [0.3909, 0.8066],
  ],
  [
    [0.399, 0.365],
    [0.4119, 0.3871],
    [0.378, 0.4285],
    [0.3606, 0.4596],
    [0.3584, 0.4673],
    [0.3631, 0.479],
    [0.3627, 0.4875],
    [0.37, 0.4971],
    [0.3967, 0.5502],
    [0.4333, 0.6278],
    [0.4411, 0.6486],
    [0.4479, 0.6687],
    [0.4618, 0.7143],
    [0.4622, 0.7151],
    [0.4639, 0.7155],
    [0.4663, 0.7145],
    [0.4671, 0.7128],
    [0.4603, 0.0696],
  ],
  [
    [0.298, 1.0612],
    [0.2999, 1.0635],
    [0.3027, 1.0653],
    [0.3058, 1.0657],
    [0.3092, 1.0645],
    [0.3117, 1.0622],
    [0.313, 1.0594],
    [0.313, 1.0562],
    [0.3111, 1.0474],
    [0.3095, 1.0309],
    [0.3106, 1.0204],
    [0.3095, 1.0171],
    [0.3077, 1.0149],
    [0.3058, 1.0135],
    [0.3035, 1.0129],
    [0.2996, 1.0128],
    [0.2959, 1.0147],
    [0.2939, 1.0179],
    [0.2933, 1.0219],
    [0.2962, 1.0328],
    [0.2976, 1.0482],
    [0.2974, 1.0585],
  ],
  [
    [0.3428, 1.052],
    [0.3447, 1.0543],
    [0.3476, 1.0561],
    [0.3506, 1.0565],
    [0.354, 1.0553],
    [0.3566, 1.053],
    [0.3579, 1.0502],
    [0.3578, 1.047],
    [0.3559, 1.0382],
    [0.3544, 1.0217],
    [0.3554, 1.0112],
    [0.3543, 1.0079],
    [0.3525, 1.0058],
    [0.3506, 1.0043],
    [0.3484, 1.0037],
    [0.3445, 1.0036],
    [0.3407, 1.0055],
    [0.3387, 1.0087],
    [0.3381, 1.0127],
    [0.3411, 1.0237],
    [0.3425, 1.039],
    [0.3422, 1.0493],
  ],
  [
    [0.2367, 0.8244],
    [0.2316, 0.8509],
    [0.2287, 0.8734],
    [0.2271, 0.8944],
    [0.2264, 0.9162],
    [0.2269, 0.9367],
    [0.2291, 0.9533],
    [0.2334, 0.9662],
    [0.2395, 0.9753],
    [0.2462, 0.9808],
    [0.252, 0.9841],
    [0.2574, 0.9861],
    [0.2591, 0.986],
    [0.2602, 0.9846],
    [0.262, 0.9632],
    [0.279, 0.9226],
    [0.2886, 0.8773],
  ],
  [
    [0.0782, 0.3373],
    [0.0748, 0.3034],
    [0.0727, 0.2561],
    [0.0739, 0.2509],
    [0.0769, 0.2471],
    [0.1146, 0.2234],
    [0.1193, 0.2215],
    [0.1236, 0.2218],
    [0.1271, 0.2237],
    [0.1296, 0.2279],
    [0.1296, 0.2342],
    [0.1241, 0.2516],
  ],
  [
    [0.3978, 0.6695],
    [0.3947, 0.6686],
    [0.3916, 0.6693],
    [0.3893, 0.6715],
    [0.3883, 0.6745],
    [0.3888, 0.6777],
    [0.3952, 0.6892],
    [0.3985, 0.6965],
    [0.4073, 0.7195],
  ],
  [
    [0.4, 0.6719],
    [0.438, 0.7995],
    [0.4382, 0.8015],
    [0.4368, 0.8045],
    [0.4354, 0.8057],
    [0.31, 0.8381],
  ],
  [
    [0.3261, 0.2237],
    [0.3296, 0.2218],
    [0.3338, 0.2215],
    [0.3386, 0.2234],
    [0.3763, 0.2471],
    [0.3793, 0.2509],
    [0.3805, 0.2561],
    [0.3784, 0.3032],
    [0.3766, 0.3239],
  ],
  [
    [0.3755, 0.3366],
    [0.3735, 0.3428],
    [0.3697, 0.344],
    [0.3656, 0.3435],
    [0.3625, 0.3416],
    [0.3585, 0.3343],
    [0.3542, 0.3219],
  ],
  [
    [0.2474, 1.0167],
    [0.2595, 1.0175],
    [0.2627, 1.0204],
    [0.264, 1.0242],
    [0.2631, 1.0343],
    [0.2646, 1.051],
  ],
  [
    [0.0876, 0.3435],
    [0.0907, 0.3416],
    [0.0947, 0.3343],
    [0.0997, 0.3199],
  ],
  [
    [0.3603, 0.7344],
    [0.3565, 0.7251],
    [0.3492, 0.7115],
    [0.3437, 0.7057],
  ],
  [
    [0.4441, 0.3521],
    [0.4406, 0.3626],
    [0.4344, 0.372],
  ],
  [
    [0.3236, 0.2279],
    [0.3236, 0.2342],
    [0.3284, 0.2495],
  ],
];

// Named device positions (meters); flipper angles in degrees. Traced from the original art except where a
// comment below says otherwise: popR (pop bodies shrunk to 29 mm, T4) and standL[2] (the third left standup,
// moved out of the left orbit's return path - see LEFT_ORBIT_EXIT_GUIDE).
const LAYOUT = {
  flipperL: {
    pivot: [
      0.1423,
      0.1489
    ],
    rest: -32,
    end: 33
  },
  flipperR: {
    pivot: [
      0.3066,
      0.1489
    ],
    rest: 212,
    end: 147
  },
  flipperU: {
    pivot: [
      0.4011,
      0.5389
    ],
    rest: -123,
    end: -163.5
  },
  bat: {
    len: 0.0591,
    r: 0.01105
  },
  pops: [
    [
      0.2691,
      0.9514
    ],
    [
      0.2941,
      0.8716
    ],
    [
      0.3822,
      0.9075
    ]
  ],
  // Lower pop raised 30 mm so its skirt and the scoop arch admit a full 27 mm ball plus clearance.
  popR: 0.029,
  slingL: {
    face: [
      [
        0.101,
        0.321
      ],
      [
        0.126,
        0.252
      ]
    ]
  },
  slingR: {
    face: [
      [
        0.354,
        0.322
      ],
      [
        0.329,
        0.25
      ]
    ]
  },
  topLanes: [
    [
      0.278,
      1.042
    ],
    [
      0.324,
      1.037
    ],
    [
      0.369,
      1.026
    ]
  ],
  inlaneL: [
    0.0506,
    0.2878
  ],
  outlaneL: [
    0.0149,
    0.2877
  ],
  inlaneR: [
    0.3936,
    0.2658
  ],
  outlaneR: [
    0.428,
    0.2659
  ],
  kickerL: [
    0.0178,
    0.1031
  ],
  kickerR: [
    0.4309,
    0.1031
  ],
  spinner: [
    0.2112,
    0.8031
  ],
  // Left standup bank. The third target was traced at (0.0643, 0.5829), tucked against the left outer wall
  // right where the left orbit lane spits its ball out: every ball coming round the loop clipped its end cap
  // and was thrown across the table into the right outlane, and nothing else could reach it because a ball
  // cannot pass between the target and the wall. It now stands in the open playfield below the guide nose,
  // where both flippers can hit it and the orbit return runs past it to the left flipper.
  standL: [
    [
      0.1918,
      0.747
    ],
    [
      0.1277,
      0.6372
    ],
    [
      0.133,
      0.522
    ]
  ],
  standR: [
    [
      0.3946,
      0.6749
    ]
  ],
  bay: {
    x0: 0.243,
    y0: 0.69,
    x1: 0.391,
    y1: 0.807
  },
  scoop: [
    0.3042,
    0.749
  ],
  globeSpot: [
    0.149,
    0.9878
  ],
  spawn: [
    0.4799,
    0.2641
  ],
  death: [
    0.2222,
    0.0367
  ],
  rampEntry1: {
    x0: 0.085,
    x1: 0.166,
    y: 0.672
  },
  rampEntry2: {
    x0: 0.357,
    x1: 0.419,
    y: 0.702
  },
  plungerDoor: {
    x0: 0.4615,
    y0: 0.716,
    x1: 0.501,
    y1: 0.881
  }
};

// Ramp definitions (only the left "LOOP" ramp survives): rails = two polylines of [x, y] wall points
// (left, right); path = centreline [x, y, z] with z = height above the playfield, peaking at 58 mm so the
// wireform stays under the glass. Both walls and the visible deck are derived from this centreline.
const RAMP_DEFS = {
  rampL: {
    path: [
      [0.1687, 0.6614, 0.00000],
      [0.1652, 0.6691, 0.00018],
      [0.1575, 0.6814, 0.00114],
      [0.1518, 0.6921, 0.00260],
      [0.1435, 0.7066, 0.00540],
      [0.1358, 0.7200, 0.00868],
      [0.1292, 0.7331, 0.01244],
      [0.1215, 0.7481, 0.01724],
      [0.1151, 0.7616, 0.02189],
      [0.1081, 0.7769, 0.02737],
      [0.1012, 0.7922, 0.03290],
      [0.0951, 0.8058, 0.03772],
      [0.0888, 0.8214, 0.04296],
      [0.0827, 0.8370, 0.04771],
      [0.0769, 0.8528, 0.05183],
      [0.0712, 0.8686, 0.05505],
      [0.0667, 0.8828, 0.05701],
      [0.0623, 0.8978, 0.05796],
      [0.0585, 0.9135, 0.05800],
      [0.0548, 0.9299, 0.05800],
      [0.0522, 0.9444, 0.05800],
      [0.0499, 0.9610, 0.05800],
      [0.0487, 0.9758, 0.05800],
      [0.0479, 0.9911, 0.05800],
      [0.0484, 1.0074, 0.05800],
      [0.0495, 1.0217, 0.05800],
      [0.0518, 1.0384, 0.05800],
      [0.0550, 1.0516, 0.05800],
      [0.0600, 1.0676, 0.05800],
      [0.0647, 1.0801, 0.05800],
      [0.0723, 1.0951, 0.05800],
      [0.0795, 1.1065, 0.05800],
      [0.0877, 1.1173, 0.05800],
      [0.0979, 1.1246, 0.05800],
      [0.1078, 1.1285, 0.05800],
      [0.1232, 1.1318, 0.05800],
      [0.1359, 1.1320, 0.05800],
      [0.1510, 1.1310, 0.05800],
      [0.1658, 1.1274, 0.05800],
      [0.1797, 1.1235, 0.05800],
      [0.1941, 1.1177, 0.05800],
      [0.2079, 1.1106, 0.05800],
      [0.2219, 1.1028, 0.05800],
      [0.2342, 1.0939, 0.05800],
      [0.2456, 1.0849, 0.05800],
      [0.2577, 1.0732, 0.05800],
      [0.2672, 1.0630, 0.05800],
      [0.2775, 1.0497, 0.05800],
      [0.2849, 1.0383, 0.05800],
      [0.2925, 1.0233, 0.05800],
      [0.2979, 1.0101, 0.05800],
      [0.3021, 0.9960, 0.05800],
      [0.3058, 0.9796, 0.05800],
      [0.3099, 0.9632, 0.05800],
      [0.3166, 0.9448, 0.05800],
      [0.3230, 0.9292, 0.05800],
      [0.3318, 0.9122, 0.05800],
      [0.3400, 0.8975, 0.05800],
      [0.3501, 0.8821, 0.05800],
      [0.3594, 0.8681, 0.05800],
      [0.3664, 0.8576, 0.05800],
      // Separate elevated return to the right inlane; the shooter at x=.480 stays unobstructed.
      [0.400, 0.805, 0.065],
      [0.414, 0.740, 0.060],
      [0.421, 0.650, 0.052],
      [0.428, 0.550, 0.043],
      [0.430, 0.460, 0.042],
      [0.420, 0.405, 0.045],
      [0.398, 0.365, 0.042],
      [0.395, 0.320, 0.015],
      [0.395, 0.285, 0]
    ],
    exitZ: 0.036
  },
};

// Overall playfield size in meters and the ball radius (13.5 mm, a slightly small pinball).
const TABLE = { W: 0.5, L: 1.2, BALL_R: 0.0135 };
const DEG = Math.PI / 180;
/**
 * Surface materials, authored once here instead of being remapped after the fact. e is the restitution the
 * physics scales in smoothly above 0.15 m/s; mu is the Coulomb friction of an impact (rolling contacts use
 * world.rollingMu instead, so mu only shapes glancing hits).
 *
 *   rail     steel guide rails, wall polylines, lane dividers, gate, plunger rod, kickback floors/doors and the
 *            replacement guides that stand in for the retired pocket walls - lively enough to keep a ball
 *            travelling along a lane, dead enough not to rattle.
 *   rubber   sling faces, the rubbered islands (RUBBER_LOOPS) and the round posts: the bounciest surfaces.
 *   target   standup and drop target faces: firmer than rubber so a hit registers without launching the ball.
 *   pop      the pop bumper bodies; the real kick is added by the rules layer on hit:popBody<i>.
 *   rampWall the thin walls that only exist on a ramp layer - almost dead, so a ball tracks the wireform.
 */
const MATERIALS = {
  rail: { e: 0.22, mu: 0.02 },
  rubber: { e: 0.5, mu: 0.02 },
  target: { e: 0.3, mu: 0.02 },
  pop: { e: 0.35, mu: 0.02 },
  rampWall: { e: 0.09, mu: 0.035 },
};
// Long-standing aliases for the two materials the wall polylines are built from.
const MAT_RAIL = MATERIALS.rail;
const MAT_RUBBER = MATERIALS.rubber;
// Rolling resistance and air damping the whole table plays with (see World.resolve / World.integrate).
const ROLLING_MU = 0.02;
const DAMPING = 0.15;

/**
 * Top-lane dividers. The two long post outlines between the A / C / E rollover lanes were traced at their full
 * plastic width, and the trace is a couple of percent smaller than a real playfield while the ball is full
 * size: that leaves 25.9 mm between the dividers at the lane C throat and 27.2 mm at lane E, so a 27 mm ball
 * wedges in lane C and never comes out (it keeps jittering, which is why the pocket sweep scores it "moving"
 * rather than trapped) and only just squeezes through lane E. Each divider's traced x span is remapped onto a
 * narrower one about the same centre line, which opens all three lanes to 30 mm. Only x is touched: the
 * dividers keep their traced length, so the height a ball must clear to skip the lanes and loop instead - what
 * the plunger calibration is built on - is unchanged.
 */
const TOP_LANE_DIVIDERS = [
  { box: { x0: 0.29, x1: 0.315, y0: 1.008, y1: 1.07 }, from: [0.2933, 0.313], to: [0.2962, 0.30915] },
  { box: { x0: 0.336, x1: 0.36, y0: 0.998, y1: 1.06 }, from: [0.3381, 0.3579], to: [0.34075, 0.3537] },
];

/** Traced point -> built point: slimmed when it belongs to a top-lane divider, unchanged otherwise. */
function slimTopLaneDivider(point) {
  for (const divider of TOP_LANE_DIVIDERS) {
    const box = divider.box;
    if (point[0] < box.x0 || point[0] > box.x1 || point[1] < box.y0 || point[1] > box.y1) continue;
    const [fromLo, fromHi] = divider.from;
    const [toLo, toHi] = divider.to;
    const t = (point[0] - fromLo) / (fromHi - fromLo);
    return [toLo + t * (toHi - toLo), point[1]];
  }
  return point;
}

/**
 * Main flipper bat length. The traced bats (LAYOUT.bat.len = 59.1 mm) left 64 mm between the resting tips -
 * 42 mm of clear air, better than one and a half balls - and in a two-hour autoplay sample every single drain
 * went straight down that middle. 63.5 mm bats close it to 34.5 mm (1.28 balls): a ball still drains between
 * them, but it has to be aimed there, and the extra 4 mm of reach also puts the drop bank and the scoop inside
 * a wider band of flipper angles. The upper flipper keeps the traced length - it is cupped by the guard rail
 * arc at (0.3631, 0.482)-(0.4238, 0.476) and a longer bat would sit inside it.
 */
const MAIN_BAT_LEN = 0.0635;

/**
 * Left orbit return guide. The left orbit lane ends where the outer wall turns back towards the outlane, and
 * the ball used to leave on whatever the corner did to it: it clipped the fatter replacement guide's end cap
 * (a 1 mm step, now gone - both left guides are 0.8 mm like the traced walls they join) and shot off across
 * the open playfield into the flipper gap, so a full plunge went round the loop and drained having touched
 * nothing. This short rail continues the lane wall another 3 cm along its own direction, past the mouth of the
 * outlane funnel, and hands the ball to the LEFT FLIPPER (it arrives at x ~ 0.163 on the bat) the way an orbit
 * return should. It stops well above the funnel, so a ball rolling down the left flank still reaches the
 * outlane from above (tests/table-outlane.mjs T2a).
 */
const LEFT_ORBIT_EXIT_GUIDE = { a: [0.0526, 0.6756], b: [0.058, 0.652] };

// Chamfer / fillet rails added to open the rest pockets tests/table-pockets.mjs finds. Each entry is a short
// guide face; `opens` names the pocket it removes. They are appended after the retired walls are replaced.
const POCKET_FIXES = [
  // Left flank of the right orbit guide's top, run straight to the corner so the cap is a point, not a shelf.
  { a: [0.4211, 1.0437], b: [0.3948, 1.0361], r: 0.0008, opens: 'right orbit guide cap (0.420, 1.058)' },
];

/**
 * Build the whole table: returns { world, lamps, labels, ramps, meta }.
 * lamps/labels are plain descriptors for the renderer; ramps also has ramps.rampL by tag; meta
 * holds the device handles Game needs (flippers, pops, kick columns/doors, spinner, drops, plunger, kickbacks,
 * scoop eject, cabinet, bay, arch).
 */
function buildTable() {
  const world = new World();
  const T = { world, lamps: [], labels: [], ramps: [], meta: {} };
  // Thin steel guide rail (1.8 mm radius, MAT_RAIL) unless the props override radius or material.
  const addRail = (x0, y0, x1, y1, props = {}) =>
    world.add(segment(x0, y0, x1, y1, props.r ?? 0.0018, { ...MAT_RAIL, ...props }));

  // --- Traced outline and inner walls ----------------------------------------------------------------------
  // Polylines in CLOSED_LOOPS (posts, islands, lane dividers) get a closing segment back to their first point;
  // the rest are open chains. RUBBER_LOOPS are the islands faced with rubber instead of bare rail.
  const CLOSED_LOOPS = new Set([1, 3, 9, 10, 12, 13, 15, 16, 17, 18, 19, 20, 21]);
  const RUBBER_LOOPS = new Set([13, 16, 18, 19, 20, 21]);
  WALL_POLYLINES.forEach((tracedPoints, index) => {
    // Superseded by the continuous inlane feed below; the old triangular nose
    // pinched a 27 mm ball against the shooter divider.
    if (index === 20) return;
    // The scoop arch is one open bent guide. Its traced closing diagonal crossed the arch and
    // made a two-contact trap at (0.33148, 0.81555). Remove the two decorative tails as well.
    // The spinner guide ends at its standup. Remove the old curled-back tail:
    // it pinched ground balls against the now physically solid ramp entrance.
    const points = index === 7 ? tracedPoints.slice(1, -1) : index === 5 ? tracedPoints.slice(0,13) : tracedPoints;
    const material = RUBBER_LOOPS.has(index) ? MAT_RUBBER : MAT_RAIL;
    const count = points.length;
    const segmentCount = CLOSED_LOOPS.has(index) ? count : count - 1;
    for (let i = 0; i < segmentCount; i++) {
      // The legacy diagonal cut straight through the upper flipper. Its cupped guard
      // below the bat already defines this boundary; keep that open, playable assembly.
      if (index === 8 && i <= 8) continue;
      const from = slimTopLaneDivider(points[i]);
      const to = slimTopLaneDivider(points[(i + 1) % count]);
      // The tracing left a few duplicate points; skip the zero-length segments they would produce.
      if (Math.hypot(to[0] - from[0], to[1] - from[1]) < 1e-5) continue;
      world.add(segment(from[0], from[1], to[0], to[1], 8e-4, { ...material, tag: null, sourcePolyline: index }));
    }
  });

  // Cabinet frame: catch-all walls just outside the playfield on every layer (31 = all five layer bits), so a
  // ball can never leave the table even while it is riding a ramp layer.
  addRail(-0.002, 0.02, -0.002, 1.175, { tag: 'frameL', layers: 31 });
  addRail(0.502, 0.02, 0.502, 1.175, { tag: 'frameR', layers: 31 });
  addRail(-0.002, 1.18, 0.502, 1.18, { tag: 'frameT', layers: 31 });
  // The traced art had two 1.8 mm pins in the middle of top lanes C and E. Both sat 11-14 mm from the lane
  // walls, so they blocked their own lane (a 27 mm ball cannot pass either side) and a slow ball balanced on
  // the pin under the divider's overhang and never came off. They are gone: the top lanes now feed the pop nest.
  // Short stubs closing gaps the tracing left: the left outer wall foot above the outlane, and the top of the
  // right orbit rail.
  addRail(7e-4, 0.3244, 8e-4, 0.3137);
  addRail(0.4537, 0.9941, 0.4534, 0.983);
  // Posts at the tops of the top-lane dividers (the first two ride on the slimmed dividers' centre lines).
  for (const [x, y] of [[0.30268, 1.067], [0.34723, 1.057], [0.256, 1.052]]) {
    world.add(circle(x, y, 0.0022, { ...MATERIALS.rubber }));
  }

  // --- Flippers ---------------------------------------------------------------------------------------------
  // Two main flippers at the bottom and an upper-right flipper (U) that shoots the left ramp. Angles are
  // authored in degrees; the physics works in radians.
  const bat = LAYOUT.bat;
  const makeFlipper = (def, side) => new Flipper({
    pivot: vec(...def.pivot),
    // The two main bats are lengthened to MAIN_BAT_LEN to narrow the drain gap; U keeps the traced length.
    length: side === 'U' ? bat.len : MAIN_BAT_LEN,
    restAngle: def.rest * DEG,
    endAngle: def.end * DEG,
    r0: bat.r,
    r1: bat.r - .003, // tapered tip: 76.2 mm main plastic bat plus 3.2 mm rubber at either end
    side,
  });
  const flipperL = makeFlipper(LAYOUT.flipperL, 'L');
  const flipperR = makeFlipper(LAYOUT.flipperR, 'R');
  const flipperU = makeFlipper(LAYOUT.flipperU, 'U');
  world.flippers.push(flipperL, flipperR, flipperU);
  T.meta.flippers = { L: flipperL, R: flipperR, U: flipperU };

  // --- Slingshots -------------------------------------------------------------------------------------------
  // Rubber faces above the flippers; the tag lets Game fire the kicker on 'hit:slingL' / 'hit:slingR'.
  for (const [def, tag] of [[LAYOUT.slingL, 'slingL'], [LAYOUT.slingR, 'slingR']]) {
    const [p, q] = def.face;
    addRail(p[0], p[1], q[0], q[1], { r: 0.0045, tag, ...MAT_RUBBER });
  }

  // --- Pop bumpers ------------------------------------------------------------------------------------------
  // Just the solid body (LAYOUT.popR = 29 mm, the radius the renderer's skirt/cap/art ring follow). The physics
  // emits hit:popBody<i> on real contact, with its own per (collider, ball) cooldown, and Game turns that into
  // the kick and the score, so the old 43 mm trigger ring sensor is gone.
  LAYOUT.pops.forEach(([x, y], i) => {
    world.add(circle(x, y, LAYOUT.popR, { ...MATERIALS.pop, tag: 'popBody' + i, kind: 'pop' }));
    T.lamps.push({ id: 'popLamp' + i, x, y, color: 'red', big: true });
  });
  T.meta.pops = LAYOUT.pops;

  // --- Top rollover lanes A / C / E -------------------------------------------------------------------------
  const laneTags = ['laneA', 'laneC', 'laneE'];
  LAYOUT.topLanes.forEach(([x, y], i) => {
    world.addSensor(sensorCircle(x, y, 0.0155, laneTags[i]));
    T.lamps.push({ id: laneTags[i], x, y: y - 0.033, color: 'amber' });
  });

  // --- Inlane / outlane rollovers ---------------------------------------------------------------------------
  // Horizontal trip lines across each lane, and the lane insert lamps.
  world.addSensor(sensorLine(0.004, LAYOUT.outlaneL[1], 0.033, LAYOUT.outlaneL[1], 'outlaneL'));
  world.addSensor(sensorLine(0.037, LAYOUT.inlaneL[1], 0.066, LAYOUT.inlaneL[1], 'inlaneL'));
  world.addSensor(sensorLine(0.378, LAYOUT.inlaneR[1], 0.409, LAYOUT.inlaneR[1], 'inlaneR'));
  world.addSensor(sensorLine(0.412, LAYOUT.outlaneR[1], 0.446, LAYOUT.outlaneR[1], 'outlaneR'));
  // The inlane inserts are the only white lamps on the table; at full size their halo bloomed into a pair of
  // headlights above the slings, so they are marked small (which shrinks the painted insert and the halo alike).
  T.lamps.push({ id: 'inlaneLLamp', x: LAYOUT.inlaneL[0], y: 0.32, color: 'white', small: true });
  T.lamps.push({ id: 'inlaneRLamp', x: LAYOUT.inlaneR[0], y: 0.3, color: 'white', small: true });
  T.lamps.push({ id: 'outlaneLLamp', x: LAYOUT.outlaneL[0], y: 0.32, color: 'red' });
  T.lamps.push({ id: 'outlaneRLamp', x: LAYOUT.outlaneR[0], y: 0.3, color: 'red' });

  // --- Kickbacks --------------------------------------------------------------------------------------------
  // Under each outlane: a fixed floor rail (kickCol) the ball lands on, and a door (kickDoor) that starts
  // inactive and is raised by Game while the kickback is armed; the sensor detects the ball settling there.
  const kickColL = world.add(segment(0.006, 0.138, 0.033, 0.138, 0.004, { tag: 'kickColL', ...MAT_RAIL, e: .02, mu: .18 }));
  const kickColR = world.add(segment(0.416, 0.138, 0.446, 0.138, 0.004, { tag: 'kickColR', ...MAT_RAIL, e: .02, mu: .18 }));
  T.meta.kickCols = { L: kickColL, R: kickColR };
  // Each door ends level with the apron corner it hands the ball to - (0.0388, 0.1575) on the left, where the
  // apron starts its run down to the drain, and (0.4095, 0.1517) on the right - and slopes down towards it, so
  // a ball riding a raised door rolls over that corner and on down the apron. (Both doors used to stop 7-12 mm
  // BELOW those corners, leaving a notch between the blade and the corner that a slow ball wedged in: the 10 mm
  // pocket sweep found one balanced at (0.416, 0.1644).) They still sit only 9-14 mm above the kicker floor and
  // seal against both cup walls, so there is no room for a ball between a raised door and the floor: a ball
  // already sitting in the cup when the door comes up is swept out along the blade (ordinary penetration
  // resolve) instead of being locked under a lid.
  const kickDoorL = world.add(
    segment(0.002, 0.1655, 0.0388, 0.1575, 0.003, { tag: 'kickDoorL', ...MAT_RAIL, active: false }));
  const kickDoorR = world.add(
    segment(0.4445, 0.156, 0.4095, 0.1517, 0.003, { tag: 'kickDoorR', ...MAT_RAIL, active: false }));
  T.meta.kickDoors = { L: kickDoorL, R: kickDoorR };
  world.addSensor(sensorCircle(0.0195, 0.158, 0.02, 'kickback'));
  world.addSensor(sensorCircle(0.431, 0.158, 0.02, 'kickbackR'));
  T.lamps.push({ id: 'kickbackLamp', x: LAYOUT.kickerL[0] + 0.012, y: 0.155, color: 'cyan' });
  T.lamps.push({ id: 'kickbackRLamp', x: LAYOUT.kickerR[0] - 0.012, y: 0.155, color: 'cyan' });
  T.labels.push({ text: 'SAVE', x: LAYOUT.kickerL[0] + 0.012, y: 0.175, size: 0.008 });
  T.labels.push({ text: 'SAVE', x: LAYOUT.kickerR[0] - 0.012, y: 0.175, size: 0.008 });

  // --- Spinner ----------------------------------------------------------------------------------------------
  // A trip line across the left orbit lane; Game turns each crossing into spins.
  const spinner = LAYOUT.spinner;
  world.addSensor(sensorLine(spinner[0] - 0.022, spinner[1], spinner[0] + 0.022, spinner[1], 'spinner'));
  T.lamps.push({ id: 'spinnerLamp', x: spinner[0], y: spinner[1] - 0.05, color: 'cyan' });
  T.labels.push({ text: 'SPIN', x: spinner[0], y: spinner[1] - 0.075, size: 0.009 });
  T.meta.spinner = { pos: vec(spinner[0], spinner[1]) };

  // --- Standup targets --------------------------------------------------------------------------------------
  // Short rubber faces angled to face the flippers (the left bank leans one way, the right target the other).
  // standLLamp0..2 are render-only inserts: one small dark dome beside each target so the bank reads as three
  // separate targets. rules.js drives the bank lamp 'standLLamp' below instead, so these three never light.
  LAYOUT.standL.forEach(([x, y], i) => {
    addRail(x - 0.012, y + 0.01, x + 0.012, y - 0.01,
      { r: 0.004, tag: 'standL', kind: 'standup', ...MATERIALS.target });
    T.lamps.push({ id: 'standLLamp' + i, x: x + 0.016, y: y - 0.014, color: 'violet', small: true });
  });
  // The right bank is a single target, so its lamp is simply 'standRLamp' (0.3786, 0.6609).
  LAYOUT.standR.forEach(([x, y]) => {
    addRail(x - 0.012, y - 0.01, x + 0.012, y + 0.01,
      { r: 0.004, tag: 'standR', kind: 'standup', ...MATERIALS.target });
    T.lamps.push({ id: 'standRLamp', x: x - 0.016, y: y - 0.014, color: 'violet' });
  });
  // Bank-complete lamp for the left standups.
  T.lamps.push({ id: 'standLLamp', x: LAYOUT.standL[0][0], y: LAYOUT.standL[0][1] - 0.03, color: 'violet' });

  // Leave a full-ball opening beneath the upper bat at rest. The old long
  // shelf converged to 14 mm and wedged a ball at (.39131,.48356). Retain
  // only the right hood that turns SAVE shots into play; its open left end
  // gives a 30 mm passage and lets underside balls fall freely.
  addRail(.404,.471,.4238,.473,{r:.0008,kind:'upper-flipper-return-guide'});

  // Feed descending ground balls onto the upper bat's working face. The former
  // long diagonal ended beyond its tip and sent balls past the entire sweep.
  // This shorter, steeper feed clears the pivot by a full ball diameter.
  addRail(.4333,.6278,.405,.582,{r:.0008,kind:'upper-flipper-feed-guide'});
  // An axial kickback runs up the outlane, then this bent hood turns it left into open play.
  // Its top bridge seals the otherwise unreachable wedge against the shooter divider.
  const inlaneFeed=[[.4238,.473],[.445,.414],[.455,.369],[.445,.3412]];
  addRail(.4238,.473,.4603+(.4671-.4603)*(.489-.0696)/(.7128-.0696),.489,{r:.0008,kind:'kickback-return-guide'});
  for(let i=1;i<inlaneFeed.length;i++)addRail(...inlaneFeed[i-1],...inlaneFeed[i],{r:.0008,kind:'inlane-feed-guide'});

  // --- Drop targets and scoop -------------------------------------------------------------------------------
  // Three drop targets across the front edge of the bay; Game knocks them down / resets them by toggling
  // active. The scoop (lock / cabinet award hole) sits inside the bay behind them.
  const bay = LAYOUT.bay;
  T.meta.drops = [];
  // The bank sits 15 mm right of where it was traced. Its left end used to stand 24 mm from the standL0 target
  // that guards the other side of the lane up to the spinner and the pop nest, leaving a 23 mm window for the
  // ball's centre - about two degrees of flipper angle - which is why the spinner registered five times in two
  // hours of autoplay. At x0 = 0.261 that window is 38 mm and the pop nest is reachable from both flippers; the
  // bank still spans the scoop at x 0.3042, so the hole stays behind the targets.
  [[0.261, 0.285], [0.291, 0.315], [0.321, 0.345]].forEach(([x0, x1], i) => {
    const drop = world.add(
      segment(x0, bay.y0, x1, bay.y0, 0.0045, { tag: 'drop' + i, ...MATERIALS.target, kind: 'drop' }));
    T.meta.drops.push(drop);
    T.lamps.push({ id: 'dropLamp' + i, x: (x0 + x1) / 2, y: bay.y0 - 0.028, color: 'amber' });
  });
  world.addSensor(sensorCircle(LAYOUT.scoop[0], LAYOUT.scoop[1], 0.017, 'scoop'));
  // Two inserts at the hole: LOCK (lit while a lock is available) in front of it, and the scoop's own award
  // lamp just behind it.
  T.lamps.push({ id: 'scoopLamp', x: 0.3042, y: 0.72, color: 'green' });
  T.lamps.push({ id: 'lockLamp', x: LAYOUT.scoop[0], y: bay.y0 - 0.028, color: 'green' });
  T.labels.push({ text: 'LOCK', x: LAYOUT.scoop[0], y: bay.y0 - 0.048, size: 0.011 });

  // --- Ramps ------------------------------------------------------------------------------------------------
  // A ramp is a physics layer: a ball that enters the mouth is moved to the ramp's layer, where only that ramp's
  // rail walls (and the all-layer frame) collide with it, and it climbs along the centreline path (z = height).
  // There is exactly one ramp left, the left "LOOP" wireform; it climbs to 65 mm, loops across the top and comes
  // back down the right-hand side into the right inlane, separately from the shooter lane.
  const rampL = {
    tag: 'rampL',
    layer: 2,
    layer2: 2,
    width: 0.044,
    path: sampleRampPath(RAMP_DEFS.rampL.path),
    authoredPath: RAMP_DEFS.rampL.path,
    color: 'cyan',
    entry: RAMP_DEFS.rampL.path[0],
    exit: RAMP_DEFS.rampL.path[RAMP_DEFS.rampL.path.length - 1],
  };
  {
    computeRampLength(rampL);
    rampL.frames = rampFrames(rampL);
    rampL.entryBaseEnd = rampL.frames.findIndex(f=>f.p[2]>=.032);
    rampL.returnBaseStart = rampL.frames.findIndex((f,i)=>i>rampL.frames.length/2&&f.p[2]<.032);
    const baseEnd=rampL.frames[rampL.entryBaseEnd];
    const orbitJoin=[.0594+(.1078-.0594)*(.8363-.818)/(.8363-.6836),.818];
    rampL.baseWing=[orbitJoin,[baseEnd.p[0]+baseEnd.n[0]*baseEnd.width/2,baseEnd.p[1]+baseEnd.n[1]*baseEnd.width/2],
      [baseEnd.p[0]-baseEnd.n[0]*baseEnd.width/2,baseEnd.p[1]-baseEnd.n[1]*baseEnd.width/2]];
    addRail(...orbitJoin,...rampL.baseWing[2],{r:.0008,kind:'ramp-base-guide'});
    // Close the shallow notch where the return housing meets the sling plastic.
    // Its downhill leftward lip sheds ground balls onto the sling's outside face.
    const returnFoot=rampL.frames[rampL.returnBaseStart];
    rampL.returnWing=[[returnFoot.p[0]+returnFoot.n[0]*returnFoot.width/2,returnFoot.p[1]+returnFoot.n[1]*returnFoot.width/2],[.3697,.344],[.3755,.3366]];
    world.add(segment(...rampL.returnWing[0],...rampL.returnWing[1],.0008,
      {layers:1,...MATERIALS.rail,tag:'rampReturnWingWall'}));
    addRampSurfaceWalls(world, rampL);
    const first=rampL.frames[0];
    const mouthLeft=[first.p[0]+first.n[0]*first.width/2,first.p[1]+first.n[1]*first.width/2];
    const mouthRight=[first.p[0]-first.n[0]*first.width/2,first.p[1]-first.n[1]*first.width/2];
    world.addSensor(sensorLine(...mouthLeft,...mouthRight,rampL.tag+'Enter',{pad:-TABLE.BALL_R-.0015}));
    rampL.mouthW=first.width;
    rampL.mouthCenter=[first.p[0],first.p[1]];
    // Award only after the ball crosses the physical end of the return.
    T.ramps.push(rampL);
    T.ramps[rampL.tag] = rampL;
  }
  T.lamps.push({ id: 'rampLArrow', x: 0.155, y: 0.63, color: 'cyan', shape: 'arrow', rot: 55 });
  T.labels.push({ text: 'LOOP', x: 0.15, y: 0.605, size: 0.01 });

  // --- Shooter lane -----------------------------------------------------------------------------------------
  // The gate at the top of the lane is one-way: a collider with oneWay is ignored while the ball moves along
  // that direction, so the ball leaves the lane upward but cannot fall back in. The plunger rod tip is a fixed
  // one-way floor the seated ball rests on (passable from below only); the launch is an impulse from
  // Game.launchSeated, so the rod collider itself never moves - only the rendered rod animates.
  const door = LAYOUT.plungerDoor;
  addRail(door.x0, door.y0, door.x1, door.y0 + 0.024, { tag: 'gate', oneWay: vec(-0.35, 0.94) });
  world.addSensor(sensorCircle(0.48, 0.2585, 0.0155, 'shooterSeat'));
  addRail(0.4635, 0.2445, 0.4985, 0.2445, { r: 0.004, tag: 'plungerRod', oneWay: vec(0, 1) });

  // --- Orbit and drain sensors ------------------------------------------------------------------------------
  // The two orbit lanes are the only ways around the top of the playfield, and they are two ends of the SAME
  // loop: a ball sent up one lane comes over the arch and back down the other. The trip lines therefore sit in
  // the lanes themselves, not in the dead-end feed beside the pop nest.
  //   orbitL  x 0.004..0.046  the left lane, between the outer wall and the left ramp foot.
  //   orbitR  x 0.452..0.500  the OUTER right lane, between the shooter-lane divider (x 0.4515 at this height)
  //           and the right outer wall. The inner right channel (x 0.41..0.43) is not a lane at all: it is
  //           capped by the guide island at y 0.978 and only feeds the pop nest, so nothing trips there.
  // Game scores a ball travelling UP either lane as an orbit shot (the trip back down the other lane is the
  // same shot, and the plunge's own climb is not one) and lights the arrows below the trip lines.
  world.addSensor(sensorLine(0.004, 0.95, 0.046, 0.95, 'orbitL'));
  world.addSensor(sensorLine(0.452, 0.95, 0.5, 0.95, 'orbitR'));
  // Inserts and legends belong in the open ground lanes, clear of the new apron
  // on the left and the elevated return on the right.
  T.lamps.push({ id: 'orbitLArrow', x: 0.055, y: 0.78, color: 'cyan', shape: 'arrow', rot: 0 });
  T.lamps.push({ id: 'orbitRArrow', x: 0.480, y: 0.78, color: 'cyan', shape: 'arrow', rot: 0 });
  T.labels.push({ text: 'ORBIT', x: 0.065, y: 0.75, size: 0.009 });
  // art.js slides a label the least distance that clears the walls of its own band, so this one is painted in
  // the 77 mm lane at about x 0.439 rather than on the guide at 0.43.
  T.labels.push({ text: 'ORBIT', x: 0.480, y: 0.75, size: 0.009 });
  // The drain spans the full width below the flippers on every layer.
  world.addSensor(sensorLine(-0.02, 0.033, 0.52, 0.033, 'drain', { layers: 31 }));

  // --- Inserts, artwork labels and general illumination -----------------------------------------------------
  T.lamps.push({ id: 'shootAgain', x: 0.222, y: 0.135, color: 'red' });
  T.labels.push({ text: 'SHOOT AGAIN', x: 0.222, y: 0.080, size: 0.0095 });
  T.labels.push({ text: 'TOKENS', x: 0.25, y: 0.5, size: 0.01 });
  T.labels.push({ text: 'BONUS X', x: 0.25, y: 0.44, size: 0.009 });
  // Three TOKEN inserts and the five BONUS X multipliers, above their labels.
  [0.221, 0.25, 0.279].forEach((x, i) => T.lamps.push({ id: 'token' + i, x, y: 0.515, color: 'amber' }));
  for (let i = 0; i < 5; i++) {
    T.lamps.push({ id: 'bonus' + (i + 2) + 'x', x: 0.206 + i * 0.022, y: 0.455, color: 'violet' });
  }
  // The jackpot insert in the middle of the playfield, lit through multiball.
  T.lamps.push({ id: 'jackpotLamp', x: 0.25, y: 0.62, color: 'red', big: true });
  T.labels.push({ text: 'X-ARCADE', x: 0.25, y: 0.58, size: 0.016 });
  T.labels.push({ text: 'TOKEN STORM', x: 0.25, y: 0.545, size: 0.011 });
  // Five pairs of warm GI lamps up each side rail.
  for (let i = 0; i < 5; i++) {
    const y = 0.2 + i * 0.19;
    T.lamps.push({ id: 'giL' + i, x: 0.01, y, color: 'warm', gi: true });
    T.lamps.push({ id: 'giR' + i, x: 0.49, y, color: 'warm', gi: true });
  }

  // --- Device metadata for Game -----------------------------------------------------------------------------
  // Both manual and automatic launches strike a stationary ball. The preloaded spring
  // spans soft returns, the three skill lanes, and full orbit shots over its 80 mm stroke.
  // Match spring energy to the rolling ball's translational + rotational inertia.
  // Keeping the former sliding-ball speeds made every pull overshoot the top lanes.
  const rollingLaunchScale=Math.sqrt(5/7);
  T.meta.plunger = { seat: vec(0.48, 0.2585), maxV: 1.67*rollingLaunchScale, minV: 1.38*rollingLaunchScale };
  T.meta.kickback = { pos: vec(0.0195, 0.16), v: vec(0.1, 3.5) };
  T.meta.kickbackR = { pos: vec(0.431, 0.16), v: vec(0, 3.5) };
  // Scoop eject: the ball reappears just below the bay's front edge and is thrown down-left toward the flippers.
  T.meta.scoopEject = { pos: vec(LAYOUT.scoop[0], bay.y0 - 0.02), v: vec(-1.25, -1.9) };
  // Mini arcade cabinet footprint (upper left) and the toy's height.
  T.meta.cabinet = { x0: 0.099, y0: 0.938, x1: 0.199, y1: 1.038, z: 0.105 };
  T.meta.bay = bay;
  // Back arch (inner and outer radii) framing the pop bumper nest.
  T.meta.arch = { c: vec(0.25, 0.86), R: 0.24, outerC: vec(0.25, 0.83), outerR: 0.27 };

  // --- Retired pocket walls, replacement guides and chamfers ------------------------------------------------
  /**
   * Five traced wall sections are retired because each formed a concave pocket where a ball could come to rest
   * in a two-contact equilibrium with no flipper able to reach it. Every segment matching one of the predicates
   * below is dropped from the collider list, and a single continuous guide face between the same neighbouring
   * endpoints takes its place (REPLACEMENT_GUIDES / POCKET_FIXES, appended in that order and rendered as
   * ordinary rails).
   *
   * The left island's own three-segment tip is NOT retired: it is the divider between the left inlane and the
   * left outlane, and replacing it with a flat face sealed the outlane from above, so the only way in was from
   * below and every entry turned into an endless kickback loop. The tip is a point again; rolling resistance
   * (world.rollingMu) is what keeps a ball from balancing on it.
   */
  function retirePocketWalls(world) {
    // 1. The 24-segment concave scallop of the left outer wall between the left ramp foot and the outlane guide:
    //    every plain wall segment with BOTH endpoints inside this box is a pocket wall. The box reaches up to
    //    y 0.68 so the short spur (0.0667, 0.6048)-(0.0526, 0.6756) goes too; leaving it behind would poke 2 cm
    //    into the playfield in front of the replacement guide and make a fresh V.
    const LEFT_POCKET_BOX = { x0: 0.023, x1: 0.073, y0: 0.492, y1: 0.68 };

    // 2. The left slingshot rest pocket: the short shelf ending at (0.0493, 0.3855) plus the rising outer wall
    //    above the left outlane hold a ball at roughly (0.033, 0.397). Nine short segments, matched by endpoints.
    const TRAP_WALL_ENDPOINTS = [
      [[0.0007, 0.3244], [0.001, 0.3366]],
      [[0.001, 0.3366], [0.0033, 0.3486]],
      [[0.0033, 0.3486], [0.0075, 0.3589]],
      [[0.0075, 0.3589], [0.0118, 0.3671]],
      [[0.0118, 0.3671], [0.0169, 0.3743]],
      [[0.0169, 0.3743], [0.0244, 0.3824]],
      [[0.0244, 0.3824], [0.0482, 0.3839]],
      [[0.0482, 0.3839], [0.0493, 0.3855]],
      [[0.0493, 0.3855], [0.0331, 0.4928]],
    ];

    // 3. The lower 3 cm of the divider that runs up-left from the standL0 guide nose closed the bottom of a
    //    downward-converging funnel: a ball that rolled in wedged between the divider, the guide's descending
    //    face and the target and could never leave (the biggest cluster in the pocket sweep). POCKET_FIXES puts
    //    back the upper part of the divider, leaving a 3 cm mouth at the bottom the ball drops through.
    const FUNNEL_DIVIDER_ENDPOINTS = [
      [[0.1602, 0.7468], [0.1292, 0.8081]],
    ];

    // 4. The top of the right orbit guide was a 1.6 mm flat cap between two flanks, and a ball dropping on it
    //    balanced there with the contact normal exactly vertical. Both faces go and POCKET_FIXES rebuilds the
    //    left flank straight to the corner at (0.4211, 1.0437), turning the cap into a point.
    const FLAT_CAP_ENDPOINTS = [
      [[0.4211, 1.0437], [0.4195, 1.0437]],
      [[0.4195, 1.0437], [0.3948, 1.0361]],
    ];

    // 5. Three short faces on the narrow right-side guide (near x 0.45, y 0.65) made a shallow inward elbow
    //    against the long outer face and could hold a ball on multiple simultaneous contacts.
    const RIGHT_GUIDE_ELBOW_ENDPOINTS = [
      [[0.4333, 0.6278], [0.4411, 0.6486]],
      [[0.4411, 0.6486], [0.4479, 0.6687]],
      [[0.4479, 0.6687], [0.4618, 0.7143]],
    ];

    // The single smooth guide faces that replace sections 1, 2 and 5, in the order they are appended;
    // sections 3 and 4 are rebuilt shorter by POCKET_FIXES instead.
    const REPLACEMENT_GUIDES = [
      // Left outer wall: one straight face between the two surviving ends of the scallop. (The shipped build ran
      // this face out to the cabinet frame at (-0.002, 0.62), which left a 6 cm notch above it and a dead wedge
      // outside the playfield that balls could roll into and stall in.) Radius 0.8 mm, the same as the traced
      // wall it meets at (0.0526, 0.6756): at the 1.8 mm the guides used to carry, its end cap stood 1 mm proud
      // of the lane wall above it and punched every descending orbit ball sideways into the middle.
      { a: [0.0331, 0.4928], b: [0.0526, 0.6756], r: 0.0008, kind: 'physical-pocket-path' },
      // Left sling rest pocket: one face from the outlane wall foot up to the same guide top.
      { a: [0.0007, 0.3244], b: [0.0331, 0.4928], r: 0.0008, kind: 'bumper-path-wall' },
      // Right-side guide: one continuous downhill face between the elbow's outer endpoints.
      { a: [0.4333, 0.6278], b: [0.4618, 0.7143], r: 0.0008, kind: 'right-guide-smooth-wall' },
    ];

    /** True when a collider endpoint coincides with an authored [x, y] point (1e-6 tolerance). */
    function samePoint(point, expected) {
      return Math.abs(point.x - expected[0]) < 1e-6 && Math.abs(point.y - expected[1]) < 1e-6;
    }

    /** True when a segment collider spans one of the given endpoint pairs, in either direction. */
    function matchesEndpoints(collider, endpointPairs) {
      if (collider.type !== 'seg') return false;
      return endpointPairs.some(([a, b]) =>
        (samePoint(collider.a, a) && samePoint(collider.b, b)) ||
        (samePoint(collider.a, b) && samePoint(collider.b, a)));
    }

    /** True for the plain wall segments lying entirely inside the left pocket box. */
    function isLeftPocketWall(collider) {
      if (collider.type !== 'seg' || collider.kind !== 'wall' || !collider.a || !collider.b) return false;
      const box = LEFT_POCKET_BOX;
      return [collider.a, collider.b].every((p) => p.x >= box.x0 && p.x <= box.x1 && p.y >= box.y0 && p.y <= box.y1);
    }

    /** True for any collider belonging to one of the five retired wall sections. */
    function isRetiredWall(collider) {
      return isLeftPocketWall(collider) || matchesEndpoints(collider, TRAP_WALL_ENDPOINTS) ||
        matchesEndpoints(collider, FUNNEL_DIVIDER_ENDPOINTS) ||
        matchesEndpoints(collider, FLAT_CAP_ENDPOINTS) ||
        matchesEndpoints(collider, RIGHT_GUIDE_ELBOW_ENDPOINTS);
    }

    world.colliders = world.colliders.filter((collider) => !isRetiredWall(collider));
    for (const guide of REPLACEMENT_GUIDES) {
      world.add(segment(guide.a[0], guide.a[1], guide.b[0], guide.b[1], guide.r,
        { ...MATERIALS.rail, kind: guide.kind }));
    }
  }
  retirePocketWalls(world);

  // Chamfers and fillets that open the remaining rest pockets found by tests/table-pockets.mjs. Each one is a
  // short guide rail (the renderer draws it like any other rail); none of them pushes a ball, they just remove
  // the two-contact equilibria a resting ball could find. See POCKET_FIXES for what each one opens.
  for (const fix of POCKET_FIXES) {
    addRail(fix.a[0], fix.a[1], fix.b[0], fix.b[1], { r: fix.r ?? 0.0018, kind: 'chamfer' });
  }

  // The left orbit's return guide (see LEFT_ORBIT_EXIT_GUIDE). Added after retirePocketWalls because both of
  // its endpoints sit inside LEFT_POCKET_BOX and a plain wall there would be filtered out again.
  addRail(LEFT_ORBIT_EXIT_GUIDE.a[0], LEFT_ORBIT_EXIT_GUIDE.a[1],
    LEFT_ORBIT_EXIT_GUIDE.b[0], LEFT_ORBIT_EXIT_GUIDE.b[1], { r: 0.0008, kind: 'orbit-return-guide' });

  world.rollingMu = ROLLING_MU;
  world.damping = DAMPING;
  return T;
}

/** Smooth a 3D ramp into <=4 mm chords. Both collision rails and the mesh use these samples. */
function sampleRampPath(points) {
  const result=[];
  for(let i=0;i<points.length-1;i++){
    const a=points[Math.max(0,i-1)],b=points[i],c=points[i+1],d=points[Math.min(points.length-1,i+2)];
    const steps=Math.max(3,Math.ceil(Math.hypot(c[0]-b[0],c[1]-b[1],c[2]-b[2])/.004));
    for(let k=0;k<steps;k++){
      const t=k/steps,t2=t*t,t3=t2*t;
      result.push(b.map((v,j)=>{
        const m0=j===2&&i===0?0:(c[j]-a[j])*.5;
        const m1=j===2&&i===points.length-2?0:(d[j]-b[j])*.5;
        return (2*t3-3*t2+1)*v+(t3-2*t2+t)*m0+(-2*t3+3*t2)*c[j]+(t3-t2)*m1;
      }));
    }
  }
  result.push([...points[points.length-1]]);return result;
}
function rampWidthAt(ramp,y) {
  // 31 mm between wire centres, 28 mm clear, inside the real right inlane.
  return ramp.width-.013*clamp((.55-y)/.20,0,1);
}
function rampFrames(ramp) {
  return ramp.path.map((p,i)=>{
    const a=ramp.path[Math.max(0,i-1)],b=ramp.path[Math.min(ramp.path.length-1,i+1)];
    const dx=b[0]-a[0],dy=b[1]-a[1],length=Math.hypot(dx,dy)||1;
    return {p,n:[-dy/length,dx/length],t:[dx/length,dy/length],width:rampWidthAt(ramp,p[1])};
  });
}
function addRampSurfaceWalls(world,ramp) {
  for(const side of [-1,1])for(let i=0;i<ramp.frames.length-1;i++){
    const a=ramp.frames[i],b=ramp.frames[i+1];
    world.add(segment(a.p[0]+a.n[0]*a.width/2*side,a.p[1]+a.n[1]*a.width/2*side,
      b.p[0]+b.n[0]*b.width/2*side,b.p[1]+b.n[1]*b.width/2*side,.0015,
      {layers:i<ramp.entryBaseEnd||i>=ramp.returnBaseStart?3:ramp.layer,...MATERIALS.rampWall,tag:ramp.tag+'Wall'}));
  }
  // The low entrance has a formed solid base: a ground ball cannot pass through
  // its floor. Close the uphill end once there is full ball-height clearance.
  for(const index of [ramp.entryBaseEnd,ramp.returnBaseStart]){
    const f=ramp.frames[index];
    world.add(segment(f.p[0]-f.n[0]*f.width/2,f.p[1]-f.n[1]*f.width/2,
      f.p[0]+f.n[0]*f.width/2,f.p[1]+f.n[1]*f.width/2,.0015,
      {layers:1,...MATERIALS.rampWall,tag:ramp.tag+'BaseWall'}));
  }
  // A return flap passes downhill traffic and stops a ground ball climbing into
  // the underside of the return. The renderer draws the same hinged blade.
  const f=ramp.frames[ramp.frames.length-1];
  ramp.returnGate=world.add(segment(f.p[0]-f.n[0]*f.width/2,f.p[1]-f.n[1]*f.width/2,
    f.p[0]+f.n[0]*f.width/2,f.p[1]+f.n[1]*f.width/2,.0008,
    {layers:1,...MATERIALS.rail,oneWay:vec(f.t[0],f.t[1]),tag:'rampReturnGate'}));
}

/** Fill ramp.cum (cumulative path length per vertex) and ramp.totalLen. */
function computeRampLength(ramp) {
  let total = 0;
  ramp.cum = [0];
  for (let i = 1; i < ramp.path.length; i++) {
    total += Math.hypot(ramp.path[i][0] - ramp.path[i - 1][0], ramp.path[i][1] - ramp.path[i - 1][1]);
    ramp.cum.push(total);
  }
  ramp.totalLen = total;
}

/** Unit tangent of the ramp path at normalised progress s (0..1). */
function rampTangent(ramp, s) {
  const target = s * ramp.totalLen;
  let i = 0;
  while (i < ramp.cum.length - 2 && ramp.cum[i + 1] < target) i++;
  const dx = ramp.path[i + 1][0] - ramp.path[i][0];
  const dy = ramp.path[i + 1][1] - ramp.path[i][1];
  const length = Math.hypot(dx, dy) || 1;
  return { x: dx / length, y: dy / length };
}

/**
 * Project a point onto the ramp path: returns { z, s, dist } (height at the closest point, normalised progress,
 * distance). With sHint, only path segments whose midpoint progress lies within `window` of the hint are
 * considered, so a ball cannot jump to a distant fold of the path; if nothing is in the window, search globally.
 */
function rampProgress(ramp, p, sHint = null, window = 0.22) {
  let bestDist2 = 1e9;
  let bestIndex = 0;
  let bestT = 0;
  for (let i = 0; i < ramp.path.length - 1; i++) {
    if (sHint !== null) {
      const midS = (ramp.cum[i] + ramp.cum[i + 1]) / 2 / ramp.totalLen;
      if (Math.abs(midS - sHint) > window) continue;
    }
    const ax = ramp.path[i][0];
    const ay = ramp.path[i][1];
    const bx = ramp.path[i + 1][0];
    const by = ramp.path[i + 1][1];
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy || 1e-9;
    let t = ((p.x - ax) * dx + (p.y - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + dx * t;
    const cy = ay + dy * t;
    const dist2 = (p.x - cx) ** 2 + (p.y - cy) ** 2;
    if (dist2 < bestDist2) {
      bestDist2 = dist2;
      bestIndex = i;
      bestT = t;
    }
  }
  if (bestDist2 === 1e9) return rampProgress(ramp, p, null);
  const z = ramp.path[bestIndex][2] + (ramp.path[bestIndex + 1][2] - ramp.path[bestIndex][2]) * bestT;
  const s = (ramp.cum[bestIndex] + (ramp.cum[bestIndex + 1] - ramp.cum[bestIndex]) * bestT) / ramp.totalLen;
  return { z, s, dist: Math.sqrt(bestDist2) };
}

/** Height gradient of the local ramp surface in playfield coordinates. */
function rampSlope(ramp, s) {
  const target = s * ramp.totalLen;
  let i = 0;
  while (i < ramp.cum.length - 2 && ramp.cum[i + 1] < target) i++;
  const a = ramp.path[i], b = ramp.path[i + 1];
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const factor = (b[2] - a[2]) / (dx * dx + dy * dy || 1);
  return { x: dx * factor, y: dy * factor };
}
